import React, { useState, useId } from 'react'
import { cn } from '@/lib/utils'
import { AlertCircleIcon } from './Icons'
import { Text } from './Text'

export interface TextFieldProps {
  /** Input label */
  label: string
  /** Visually hide the label (still accessible to screen readers) */
  labelHidden?: boolean
  /** Current value */
  value: string
  /** Change handler */
  onChange: (value: string) => void
  /** Placeholder text */
  placeholder?: string
  /** Help text below input */
  helpText?: string
  /** Error message or boolean */
  error?: string | boolean
  /** Disabled state */
  disabled?: boolean
  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number' | 'url' | 'search' | 'tel'
  /** Prefix content */
  prefix?: React.ReactNode
  /** Suffix content */
  suffix?: React.ReactNode
  /** Multiline (textarea) - true or number of rows */
  multiline?: boolean | number
  /** Autocomplete attribute */
  autoComplete?: string
  /** Max length */
  maxLength?: number
  /** Show character count */
  showCharacterCount?: boolean
  /** Input ID */
  id?: string
  /** Input name */
  name?: string
  /** Additional class name */
  className?: string
  /** Data ID for testing */
  'data-id'?: string
  /** Data testid for testing (applied to input element) */
  'data-testid'?: string
  /** Auto focus on mount */
  autoFocus?: boolean
  /** Required field */
  required?: boolean
}

export function TextField({
  label,
  labelHidden = false,
  value,
  onChange,
  placeholder,
  helpText,
  error,
  disabled = false,
  type = 'text',
  prefix,
  suffix,
  multiline = false,
  autoComplete = 'off',
  maxLength,
  showCharacterCount = false,
  id: providedId,
  name,
  className,
  'data-id': dataId,
  'data-testid': dataTestId,
  autoFocus,
  required,
}: TextFieldProps) {
  const uniqueId = useId()
  const id = providedId || uniqueId
  const helpTextId = `${id}-help`
  const errorId = `${id}-error`
  const [isFocused, setIsFocused] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange(e.target.value)
  }

  const isMultiline = Boolean(multiline)
  const rows = typeof multiline === 'number' ? multiline : 2

  // Styles
  const baseBorderColor = error ? 'border-destructive' : 'border-border'
  const focusStyles = error
    ? 'ring-1 ring-destructive border-destructive'
    : 'ring-1 ring-ring border-ring'

  const wrapperClasses = cn(
    'relative flex items-stretch w-full rounded transition-all duration-150 ease-in-out border',
    disabled ? 'bg-muted cursor-not-allowed' : 'bg-background',
    isFocused ? focusStyles : baseBorderColor,
    disabled ? 'border-border' : ''
  )

  const inputClasses = cn(
    'flex-1 w-full bg-transparent border-none outline-none text-foreground placeholder-muted-foreground',
    'disabled:cursor-not-allowed disabled:text-muted-foreground',
    'px-3 py-2 text-[14px] leading-5',
    isMultiline ? 'resize-y min-h-[72px]' : 'h-[36px]'
  )

  const prefixSuffixBase = cn(
    'flex items-center justify-center bg-muted px-3 text-muted-foreground text-sm select-none',
    disabled ? 'opacity-80' : ''
  )

  return (
    <div className={cn('w-full font-sans', className)} data-id={dataId}>
      {/* Label */}
      <div className={cn('flex justify-between items-baseline mb-1', labelHidden && 'sr-only')}>
        <Text as="label" htmlFor={id}>
          {label}
        </Text>
      </div>

      {/* Input Wrapper */}
      <div className={wrapperClasses}>
        {prefix && (
          <div
            className={cn(prefixSuffixBase, 'rounded-l border-r border-border')}
          >
            {prefix}
          </div>
        )}

        {isMultiline ? (
          <textarea
            id={id}
            name={name}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            maxLength={maxLength}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            required={required}
            data-testid={dataTestId}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={inputClasses}
            aria-invalid={Boolean(error)}
            aria-describedby={
              error ? errorId : helpText ? helpTextId : undefined
            }
          />
        ) : (
          <input
            id={id}
            name={name}
            type={type}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            required={required}
            data-testid={dataTestId}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={inputClasses}
            aria-invalid={Boolean(error)}
            aria-describedby={
              error ? errorId : helpText ? helpTextId : undefined
            }
          />
        )}

        {suffix && (
          <div
            className={cn(prefixSuffixBase, 'rounded-r border-l border-border')}
          >
            {suffix}
          </div>
        )}
      </div>

      {/* Bottom Text Area (Error, Help Text, Character Count) */}
      <div className="mt-1 flex justify-between items-start gap-4">
        <div className="flex-1">
          {error && typeof error === 'string' && (
            <div
              id={errorId}
              className="flex items-center gap-1.5 text-destructive text-xs"
            >
              <AlertCircleIcon className="w-3 h-3 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {!error && helpText && (
            <div id={helpTextId} className="text-muted-foreground text-xs">
              {helpText}
            </div>
          )}
        </div>

        {showCharacterCount && maxLength && (
          <div className="text-muted-foreground text-xs tabular-nums shrink-0">
            {value.length} / {maxLength}
          </div>
        )}
      </div>
    </div>
  )
}
